import { useState, useEffect } from "react";
import { X, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import apiFetch from "../apiFetch";
import "../styles/sessionReviewPanel.css";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function PassageBlock({ text, title = "Passage" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="srp-passage-block">
      <button className="srp-passage-toggle" onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="srp-passage-text">{text}</div>}
    </div>
  );
}

function ComprehensionReview({ rj }) {
  const { passage, questions, userAnswers, markingResults, bookTitle } = rj;
  const results = markingResults?.results || [];

  return (
    <div className="srp-body">
      {bookTitle && <p className="srp-book-title">📖 {bookTitle}</p>}
      {passage && <PassageBlock text={passage} />}
      <ol className="srp-qa-list">
        {(questions || []).map((q, i) => {
          const res      = results[i] || {};
          const marks    = res.marksAwarded ?? "?";
          const maxMarks = q.marks ?? "?";
          const isFullMark = typeof marks === "number" && marks === maxMarks;
          const isPartial  = typeof marks === "number" && marks > 0 && marks < maxMarks;
          const chipCls = isFullMark ? "srp-chip--green" : isPartial ? "srp-chip--amber" : "srp-chip--red";
          return (
            <li key={i} className="srp-qa-item">
              <div className="srp-qa-q">Q{i + 1}. {q.question}</div>
              <div className="srp-qa-answer">
                <span className="srp-qa-label">Your answer:</span>
                <span className="srp-qa-text">{userAnswers?.[i] || "No answer provided"}</span>
              </div>
              <div className="srp-qa-marks">
                <span className={`srp-marks-chip ${chipCls}`}>
                  {marks}/{maxMarks} marks
                </span>
              </div>
              {res.feedback && (
                <div className="srp-qa-feedback">{res.feedback}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function MCQReview({ rj }) {
  const { passage, questions, userAnswers } = rj;

  return (
    <div className="srp-body">
      {passage && <PassageBlock text={passage} />}
      <ol className="srp-qa-list">
        {(questions || []).map((q, i) => {
          const userAns  = userAnswers?.[i] ?? userAnswers?.[String(i)];
          const isCorrect = userAns === q.correct;
          return (
            <li key={i} className="srp-qa-item">
              <div className="srp-qa-q">Q{i + 1}. {q.question}</div>
              <div className="srp-mcq-options">
                {Object.entries(q.options || {}).map(([key, text]) => {
                  const isUser  = key === userAns;
                  const isRight = key === q.correct;
                  let cls = "srp-mcq-opt";
                  if (isRight) cls += " srp-mcq-opt--correct";
                  else if (isUser && !isRight) cls += " srp-mcq-opt--wrong";
                  return (
                    <div key={key} className={cls}>
                      <span className="srp-mcq-key">{key}.</span>
                      <span>{text}</span>
                      {isRight && <CheckCircle2 size={13} className="srp-icon-right" />}
                      {isUser && !isRight && <XCircle size={13} className="srp-icon-wrong" />}
                    </div>
                  );
                })}
              </div>
              {!isCorrect && userAns && (
                <p className="srp-mcq-verdict srp-mcq-verdict--wrong">
                  You answered {userAns} · Correct answer: {q.correct}
                </p>
              )}
              {isCorrect && (
                <p className="srp-mcq-verdict srp-mcq-verdict--right">Correct!</p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function VocabTestReview({ rj }) {
  const [wordMap, setWordMap]   = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!rj?.answers?.length) { setLoading(false); return; }
    const ids = [...new Set(rj.answers.map(a => a.wordId))].join(",");
    apiFetch(`/api/vocab-test/words-by-ids?ids=${ids}`)
      .then(r => r.ok ? r.json() : {})
      .then(map => { setWordMap(map); setLoading(false); })
      .catch(() => setLoading(false));
  }, [rj]);

  if (loading) return <div className="srp-loading">Loading word details…</div>;

  const answers = rj?.answers || [];
  const correctCount = answers.filter(a => a.correct).length;

  return (
    <div className="srp-body">
      <p className="srp-vocab-summary">
        {correctCount} / {answers.length} correct
      </p>
      <ul className="srp-vocab-list">
        {answers.map((a, i) => {
          const word = wordMap[a.wordId];
          return (
            <li key={i} className={`srp-vocab-row ${a.correct ? "srp-vocab--correct" : "srp-vocab--wrong"}`}>
              {a.correct
                ? <CheckCircle2 size={15} className="srp-vocab-icon srp-icon-right" />
                : <XCircle     size={15} className="srp-vocab-icon srp-icon-wrong"  />}
              <div className="srp-vocab-content">
                <span className="srp-vocab-word">{word?.word || `Word #${a.wordId}`}</span>
                {word?.definition && <span className="srp-vocab-def">{word.definition}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReviewBody({ gameType, rj }) {
  if (!rj) {
    return (
      <div className="srp-no-data">
        Detailed question review is not available for this game type.
      </div>
    );
  }

  switch (gameType) {
    case "comprehension":
    case "book_comprehension":
      return <ComprehensionReview rj={rj} />;
    case "mcq":
      return <MCQReview rj={rj} />;
    case "vocab_definitions":
    case "vocab_synonyms":
    case "vocab_antonyms":
    case "vocab_mixed":
      return <VocabTestReview rj={rj} />;
    default:
      return (
        <div className="srp-no-data">
          Detailed question review is not available for this game type.
        </div>
      );
  }
}

export default function SessionReviewPanel({ completion, gameType, sessionNum, onClose }) {
  const rj       = completion?.result_json;
  const hasScore = completion?.score != null && completion?.max_score > 0;
  const pct      = hasScore ? Math.round((completion.score / completion.max_score) * 100) : null;

  return (
    <div className="srp-overlay" onClick={onClose}>
      <div className="srp-panel" onClick={e => e.stopPropagation()}>
        <div className="srp-header">
          <div className="srp-header-info">
            <h2 className="srp-title">Session #{sessionNum} Review</h2>
            <p className="srp-sub">
              {fmtDate(completion?.completed_at)}
              {hasScore && (
                <>
                  {" · "}
                  <span className={`srp-score-chip ${pct >= 80 ? "srp-chip--green" : pct >= 60 ? "srp-chip--amber" : "srp-chip--red"}`}>
                    {completion.score}/{completion.max_score} ({pct}%)
                  </span>
                </>
              )}
            </p>
          </div>
          <button className="srp-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="srp-content">
          <ReviewBody gameType={gameType} rj={rj} />
        </div>
      </div>
    </div>
  );
}
